import { adminService } from "@/api/admin/admin.service";
import {
  TAdminAuditEvent,
  TAdminUser,
  TEffectivePermissions,
  TLoginAttempt,
  TRoleResponse,
  TUserTokens,
} from "@/api/admin/admin.types";
import { RouteLink } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { maskEmail, maskPhone } from "@/utils/maskUtils";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockOpen,
  MailCheck,
  PhoneCall,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserX,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const AUDIT_PAGE_SIZE = 20;
const LOGIN_ATTEMPTS_PAGE_SIZE = 10;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function StatusBadge({ statusId }: { statusId: number }) {
  if (statusId === 1) {
    return (
      <Badge
        variant="outline"
        className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
      >
        Active
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-destructive/30 bg-destructive/10 text-destructive"
    >
      Inactive
    </Badge>
  );
}

function VerifiedBadge({ label, verified }: { label: string; verified: boolean }) {
  return (
    <Badge
      variant="outline"
      className={
        verified
          ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
          : "border-border bg-muted text-muted-foreground"
      }
    >
      {verified && <BadgeCheck className="mr-1 h-3 w-3" />}
      {label} {verified ? "verified" : "unverified"}
    </Badge>
  );
}

type TConfirmState = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  action: (() => Promise<void>) | null;
};

const CLOSED_CONFIRM: TConfirmState = {
  open: false,
  title: "",
  description: "",
  confirmLabel: "",
  action: null,
};

type TProps = {
  userId: string;
};

export function UserDetailPage({ userId }: TProps) {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [user, setUser] = useState<TAdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContact, setShowContact] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [permissions, setPermissions] = useState<TEffectivePermissions | null>(null);
  const [availableRoles, setAvailableRoles] = useState<TRoleResponse[]>([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [isAssigningRole, setIsAssigningRole] = useState(false);

  const [tokens, setTokens] = useState<TUserTokens | null>(null);
  const [tokensLoading, setTokensLoading] = useState(true);

  const [loginAttempts, setLoginAttempts] = useState<TLoginAttempt[]>([]);
  const [attemptsTotal, setAttemptsTotal] = useState(0);
  const [attemptsPage, setAttemptsPage] = useState(1);
  const [attemptsLoading, setAttemptsLoading] = useState(true);

  const [auditEvents, setAuditEvents] = useState<TAdminAuditEvent[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(true);

  const [confirm, setConfirm] = useState<TConfirmState>(CLOSED_CONFIRM);
  const [isConfirming, setIsConfirming] = useState(false);

  const loadUser = useCallback(async () => {
    const { data, error: err } = await adminService.getUser(userId);
    if (err) {
      setError(await extractErrorMessage(err));
      setUser(null);
    } else if (data) {
      setError(null);
      setUser(data.data);
    }
    setIsLoading(false);
  }, [userId]);

  const loadPermissions = useCallback(async () => {
    const { data, error: err } = await adminService.getUserPermissions(userId);
    if (!err && data) setPermissions(data.data);
  }, [userId]);

  const loadRoles = useCallback(async () => {
    const { data, error: err } = await adminService.getRoles();
    if (!err && data && Array.isArray(data.data)) setAvailableRoles(data.data);
  }, []);

  const loadTokens = useCallback(async () => {
    setTokensLoading(true);
    const { data, error: err } = await adminService.getUserTokens(userId);
    if (!err && data) setTokens(data.data);
    setTokensLoading(false);
  }, [userId]);

  const loadLoginAttempts = useCallback(
    async (page: number) => {
      setAttemptsLoading(true);
      const { data, error: err } = await adminService.getUserLoginAttempts(userId, {
        limit: LOGIN_ATTEMPTS_PAGE_SIZE,
        offset: (page - 1) * LOGIN_ATTEMPTS_PAGE_SIZE,
      });
      if (!err && data) {
        setLoginAttempts(data.data?.items ?? []);
        setAttemptsTotal(data.data?.total_count ?? 0);
      }
      setAttemptsLoading(false);
    },
    [userId]
  );

  const loadAudit = useCallback(
    async (page: number) => {
      setAuditLoading(true);
      const { data, error: err } = await adminService.getUserAuditTrail(userId, {
        page,
        page_size: AUDIT_PAGE_SIZE,
      });
      if (!err && data) {
        setAuditEvents(data.data.events ?? []);
        setAuditTotal(data.data.total_count ?? 0);
      }
      setAuditLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    setIsLoading(true);
    void loadUser();
    void loadPermissions();
    void loadRoles();
    void loadTokens();
    void loadLoginAttempts(1);
    void loadAudit(1);
  }, [loadUser, loadPermissions, loadRoles, loadTokens, loadLoginAttempts, loadAudit]);

  const runUserAction = useCallback(
    async (
      action: string,
      request: () => ReturnType<typeof adminService.activateUser>,
      successTitle: string
    ) => {
      setActionLoading(action);
      const { error: err } = await request();
      if (err) {
        toast({ variant: "destructive", title: await extractErrorMessage(err) });
      } else {
        toast({ title: successTitle });
        await Promise.all([loadUser(), loadAudit(auditPage)]);
      }
      setActionLoading(null);
    },
    [toast, loadUser, loadAudit, auditPage]
  );

  const handleDelete = () => {
    setConfirm({
      open: true,
      title: "Delete User",
      description:
        "Are you sure you want to permanently delete this user? This action cannot be undone.",
      confirmLabel: "Delete",
      action: async () => {
        const { error: err } = await adminService.deleteUser(userId);
        if (err) {
          toast({ variant: "destructive", title: await extractErrorMessage(err) });
        } else {
          toast({ title: "User deleted" });
          void navigate({ to: "/app/admin/users" });
        }
      },
    });
  };

  const handleDeactivate = () => {
    setConfirm({
      open: true,
      title: "Deactivate User",
      description: "The user will no longer be able to sign in until reactivated.",
      confirmLabel: "Deactivate",
      action: async () => {
        await runUserAction("deactivate", () => adminService.deactivateUser(userId), "User deactivated");
      },
    });
  };

  const handleAssignRole = async () => {
    if (!selectedRole) return;
    setIsAssigningRole(true);
    const { error: err } = await adminService.assignRole(userId, selectedRole);
    if (err) {
      toast({ variant: "destructive", title: await extractErrorMessage(err) });
    } else {
      toast({ title: `Role "${selectedRole}" assigned` });
      setSelectedRole("");
      await Promise.all([loadUser(), loadPermissions()]);
    }
    setIsAssigningRole(false);
  };

  const handleRevokeRole = (roleName: string) => {
    setConfirm({
      open: true,
      title: "Revoke Role",
      description: `Are you sure you want to revoke the "${roleName}" role from this user?`,
      confirmLabel: "Revoke",
      action: async () => {
        const { error: err } = await adminService.revokeRole(userId, roleName);
        if (err) {
          toast({ variant: "destructive", title: await extractErrorMessage(err) });
        } else {
          toast({ title: `Role "${roleName}" revoked` });
          await Promise.all([loadUser(), loadPermissions()]);
        }
      },
    });
  };

  const handleRevokeToken = (tokenId: string) => {
    setConfirm({
      open: true,
      title: "Revoke Token",
      description: "The session associated with this token will be terminated immediately.",
      confirmLabel: "Revoke",
      action: async () => {
        const { error: err } = await adminService.revokeAuthToken(tokenId);
        if (err) {
          toast({ variant: "destructive", title: await extractErrorMessage(err) });
        } else {
          toast({ title: "Token revoked" });
          await loadTokens();
        }
      },
    });
  };

  const handleRevokeAllTokens = () => {
    setConfirm({
      open: true,
      title: "Revoke All Tokens",
      description:
        "All auth and refresh tokens for this user will be revoked. The user will be signed out everywhere.",
      confirmLabel: "Revoke All",
      action: async () => {
        const { error: err } = await adminService.revokeAllUserTokens(userId);
        if (err) {
          toast({ variant: "destructive", title: await extractErrorMessage(err) });
        } else {
          toast({ title: "All tokens revoked" });
          await loadTokens();
        }
      },
    });
  };

  const handleConfirm = async () => {
    if (!confirm.action) return;
    setIsConfirming(true);
    try {
      await confirm.action();
    } finally {
      setIsConfirming(false);
      setConfirm(CLOSED_CONFIRM);
    }
  };

  const auditTotalPages = Math.max(1, Math.ceil(auditTotal / AUDIT_PAGE_SIZE));

  const goToAuditPage = (page: number) => {
    setAuditPage(page);
    void loadAudit(page);
  };

  const attemptsTotalPages = Math.max(1, Math.ceil(attemptsTotal / LOGIN_ATTEMPTS_PAGE_SIZE));

  const goToAttemptsPage = (page: number) => {
    setAttemptsPage(page);
    void loadLoginAttempts(page);
  };

  const isActive = user?.status_id === 1;

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <RouteLink to="/app/admin/users">
              <Button
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Users
              </Button>
            </RouteLink>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">User Details</h1>
              {user && <p className="mt-1 text-xs text-muted-foreground">{user.email}</p>}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void loadUser();
              void loadPermissions();
              void loadTokens();
              void loadLoginAttempts(attemptsPage);
              void loadAudit(auditPage);
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/4" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <p className="text-destructive">Error: {error}</p>
              <Button
                variant="outline"
                onClick={() => {
                  setIsLoading(true);
                  void loadUser();
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : user ? (
          <>
            {/* Profile header card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-semibold text-foreground">
                        {user.first_name} {user.last_name}
                      </h2>
                      <StatusBadge statusId={user.status_id} />
                      <VerifiedBadge
                        label="Email"
                        verified={user.email_verified}
                      />
                      <VerifiedBadge
                        label="Phone"
                        verified={user.phone_verified}
                      />
                      {user.two_factor_enabled && (
                        <Badge variant="secondary">
                          <ShieldCheck className="mr-1 h-3 w-3" />
                          2FA
                        </Badge>
                      )}
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>{showContact ? user.email : maskEmail(user.email)}</span>
                        <span>•</span>
                        <span>
                          {user.phone_number
                            ? showContact
                              ? user.phone_number
                              : maskPhone(user.phone_number)
                            : "No phone"}
                        </span>
                        <button
                          onClick={() => setShowContact((p) => !p)}
                          className="rounded p-1 text-muted-foreground hover:text-foreground"
                          aria-label={showContact ? "Hide contact details" : "Show contact details"}
                          title={showContact ? "Hide contact details" : "Show contact details"}
                        >
                          {showContact ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p>
                        Created {fmtDate(user.created_at)} • Last login {fmtDate(user.last_login)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap gap-2 border-t border-border pt-4">
                  {isActive ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeactivate}
                      disabled={actionLoading !== null}
                    >
                      <UserX className="mr-2 h-4 w-4" />
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void runUserAction("activate", () => adminService.activateUser(userId), "User activated")
                      }
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === "activate" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <UserCheck className="mr-2 h-4 w-4" />
                      )}
                      Activate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void runUserAction("unlock", () => adminService.unlockUser(userId), "User unlocked")
                    }
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === "unlock" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <LockOpen className="mr-2 h-4 w-4" />
                    )}
                    Unlock
                  </Button>
                  {!user.email_verified && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void runUserAction(
                          "verify-email",
                          () => adminService.verifyUserEmail(userId),
                          "Email verified"
                        )
                      }
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === "verify-email" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <MailCheck className="mr-2 h-4 w-4" />
                      )}
                      Verify Email
                    </Button>
                  )}
                  {!user.phone_verified && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void runUserAction(
                          "verify-phone",
                          () => adminService.verifyUserPhone(userId),
                          "Phone verified"
                        )
                      }
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === "verify-phone" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <PhoneCall className="mr-2 h-4 w-4" />
                      )}
                      Verify Phone
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    disabled={actionLoading !== null}
                    className="ml-auto"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Roles & permissions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  Roles & Permissions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Roles</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {(user.roles ?? []).length === 0 ? (
                      <span className="text-sm text-muted-foreground">No roles assigned</span>
                    ) : (
                      (user.roles ?? []).map((role) => (
                        <span
                          key={role.role_id}
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-xs font-medium text-foreground"
                        >
                          {role.name}
                          <button
                            onClick={() => handleRevokeRole(role.name)}
                            className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                            aria-label={`Revoke role ${role.name}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      aria-label="Select role to assign"
                    >
                      <option value="">Select a role…</option>
                      {availableRoles
                        .filter((role) => !(user.roles ?? []).some((r) => r.name === role.name))
                        .map((role) => (
                          <option
                            key={role.role_id}
                            value={role.name}
                          >
                            {role.name}
                          </option>
                        ))}
                    </select>
                    <Button
                      size="sm"
                      onClick={() => void handleAssignRole()}
                      disabled={!selectedRole || isAssigningRole}
                    >
                      {isAssigningRole ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="mr-2 h-4 w-4" />
                      )}
                      Assign Role
                    </Button>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Effective Permissions
                    {permissions && permissions.computed_from_roles.length > 0 && (
                      <span className="ml-2 normal-case tracking-normal">
                        (from: {permissions.computed_from_roles.join(", ")})
                      </span>
                    )}
                  </p>
                  {!permissions ? (
                    <Skeleton className="h-4 w-1/2" />
                  ) : permissions.permissions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No permissions</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {permissions.permissions.map((perm) => (
                        <Badge
                          key={perm.permission_id}
                          variant="outline"
                          title={perm.description ?? `${perm.resource}:${perm.action}`}
                        >
                          {perm.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sessions / tokens */}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="h-5 w-5" />
                    Sessions & Tokens
                  </CardTitle>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRevokeAllTokens}
                    disabled={
                      tokensLoading ||
                      ((tokens?.auth_tokens ?? []).length === 0 && (tokens?.refresh_tokens ?? []).length === 0)
                    }
                  >
                    Revoke All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tokensLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        className="h-8 w-full"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px]">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Type
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Status
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Created
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Last Used
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            IP
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Expires
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(tokens?.auth_tokens ?? []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="px-3 py-6 text-center text-sm text-muted-foreground"
                            >
                              No auth tokens found
                            </td>
                          </tr>
                        ) : (
                          (tokens?.auth_tokens ?? []).map((token) => (
                            <tr
                              key={token.id}
                              className="border-b border-border last:border-0"
                            >
                              <td className="px-3 py-2 text-sm text-foreground">{token.token_type}</td>
                              <td className="px-3 py-2">
                                <Badge
                                  variant="outline"
                                  className={
                                    token.status === "active"
                                      ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                                      : "border-border bg-muted text-muted-foreground"
                                  }
                                >
                                  {token.status}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(token.created_at)}</td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(token.last_used_at)}</td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">{token.ip_address ?? "-"}</td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(token.expires_at)}</td>
                              <td className="px-3 py-2">
                                {token.status === "active" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRevokeToken(token.id)}
                                  >
                                    Revoke
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    <p className="mt-3 text-xs text-muted-foreground">
                      {(tokens?.refresh_tokens ?? []).length} refresh token
                      {(tokens?.refresh_tokens ?? []).length === 1 ? "" : "s"} on record
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Login attempts */}
            <Card>
              <CardHeader>
                <CardTitle>Login Attempts</CardTitle>
              </CardHeader>
              <CardContent>
                {attemptsLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        className="h-8 w-full"
                      />
                    ))}
                  </div>
                ) : loginAttempts.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">No login attempts found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[480px]">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Time
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Result
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            IP
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Failure Reason
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginAttempts.map((attempt) => (
                          <tr
                            key={attempt.id}
                            className="border-b border-border last:border-0"
                          >
                            <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(attempt.created_at)}</td>
                            <td className="px-3 py-2">
                              <Badge
                                variant="outline"
                                className={
                                  attempt.successful
                                    ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
                                    : "border-destructive/30 bg-destructive/10 text-destructive"
                                }
                              >
                                {attempt.successful ? "Success" : "Failed"}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{attempt.ip_address ?? "-"}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {attempt.failure_reason ?? "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex items-center justify-between gap-4 pt-3">
                      <p className="text-xs text-muted-foreground">
                        Showing {(attemptsPage - 1) * LOGIN_ATTEMPTS_PAGE_SIZE + 1}–
                        {Math.min(attemptsPage * LOGIN_ATTEMPTS_PAGE_SIZE, attemptsTotal)} of {attemptsTotal}
                      </p>
                      {attemptsTotalPages > 1 && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={attemptsPage <= 1 || attemptsLoading}
                            onClick={() => goToAttemptsPage(attemptsPage - 1)}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={attemptsPage >= attemptsTotalPages || attemptsLoading}
                            onClick={() => goToAttemptsPage(attemptsPage + 1)}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audit trail */}
            <Card>
              <CardHeader>
                <CardTitle>Audit Trail</CardTitle>
              </CardHeader>
              <CardContent>
                {auditLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        className="h-10 w-full"
                      />
                    ))}
                  </div>
                ) : auditEvents.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">No audit events found</p>
                ) : (
                  <div className="space-y-2">
                    {auditEvents.map((event, index) => (
                      <details
                        key={event.event_id ?? `${event.timestamp}-${index}`}
                        className="rounded-lg border border-border bg-card px-4 py-3"
                      >
                        <summary className="flex cursor-pointer flex-wrap items-center gap-3 text-sm">
                          <Badge variant="secondary">{event.event_type}</Badge>
                          <span className="text-xs text-muted-foreground">{fmtDate(event.timestamp)}</span>
                          {event.target_entity && (
                            <span className="text-xs text-muted-foreground">{event.target_entity}</span>
                          )}
                        </summary>
                        <div className="mt-3 space-y-2 border-t border-border pt-3 text-xs text-muted-foreground">
                          {event.ip_address && <p>IP: {event.ip_address}</p>}
                          {event.action_details && (
                            <pre className="max-h-40 overflow-auto rounded bg-muted p-2">
                              {JSON.stringify(event.action_details, null, 2)}
                            </pre>
                          )}
                        </div>
                      </details>
                    ))}

                    {auditTotalPages > 1 && (
                      <div className="flex items-center justify-between gap-4 pt-2">
                        <p className="text-sm text-muted-foreground">
                          Page {auditPage} of {auditTotalPages} ({auditTotal} events)
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={auditPage <= 1}
                            onClick={() => goToAuditPage(auditPage - 1)}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={auditPage >= auditTotalPages}
                            onClick={() => goToAuditPage(auditPage + 1)}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* Confirm dialog */}
        <Dialog
          open={confirm.open}
          onOpenChange={(open) => {
            if (!open && !isConfirming) setConfirm(CLOSED_CONFIRM);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{confirm.title}</DialogTitle>
              <DialogDescription>{confirm.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirm(CLOSED_CONFIRM)}
                disabled={isConfirming}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleConfirm()}
                disabled={isConfirming}
              >
                {isConfirming ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Working…
                  </>
                ) : (
                  confirm.confirmLabel
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
